import initCheckpoint from 'containers/pages/checklists/data/initCheckpoint';
import { cloneDeep } from 'lodash';
import { useNotificationProvider } from 'hooks';
import { useState, useRef } from 'react';

const useCheckpointTable = (
	checkpoints,
	setCheckpoints,
	checklist,
	highlightedCheckpointID,
	setHighlightedCheckpointID,
	inputValidationHelpers,
) => {
	const [newCheckpointCount, setNewCheckpointCount] = useState(1);
	const [selectAll, setSelectAll] = useState(false);

	const tableContainerRef = useRef(null); // Ref for the container

	const { notifyWarning } = useNotificationProvider();

	const scrollToBottom = () => {
		if (tableContainerRef.current) {
			tableContainerRef.current.scrollTop =
				tableContainerRef.current.scrollHeight;
		}
	};

	const handleDragEnd = (result) => {
		if (!result.destination) {
			return;
		}
		const startIndex = result.source.index;
		const endIndex = result.destination.index;
		setCheckpoints((prev) => {
			const temp = [...prev];
			const [removed] = temp.splice(startIndex, 1);
			temp.splice(endIndex, 0, removed);
			return temp;
		});
	};

	const onNewCheckpointClick = () => {
		//CREATE A NEW CHECKPOINT OBJECT
		let tempCheckpoint = cloneDeep(initCheckpoint);
		tempCheckpoint.ChecklistID = checklist ? checklist.ChecklistID : -1; //ASSIGNING PARENT CHECKLIST ID. -1 IS ASSIGNED IF IT IS A NEW CHECKLIST.
		tempCheckpoint.CheckpointID = newCheckpointCount * -1;
		tempCheckpoint.Name = `New Checkpoint ${newCheckpointCount}`;

		if (selectAll) {
			tempCheckpoint.isSelected = true;
		}

		setCheckpoints((prevState) => {
			return [...prevState, tempCheckpoint];
		});

		if (!inputValidationHelpers.checkCheckpointInputErrors()) {
			//INPUT VALIDATION IS COMPLETE, SAVING CHECKPOINT AND CHANGING THE HIGHLIGHTED CHECKPOINT.
			setHighlightedCheckpointID(tempCheckpoint.CheckpointID);

			setTimeout(() => {
				scrollToBottom();
			}, 100);
		}

		setNewCheckpointCount((prevState) => {
			return prevState + 1;
		});
	};
	const onCopyCheckpointClick = () => {
		let id = newCheckpointCount;
		const copiedCheckpoints = [];
		//COPYING THE SELECTED CHECKPOINTS:
		checkpoints.forEach((checkpoint) => {
			if (checkpoint.isSelected) {
				// copy and return the checkpoint
				let copiedCheckpoint = Object.assign({}, checkpoint); //COPYING CHECKPOINT

				//UPDATING THE ID, NAME AND THE TAG_INV_DEVICE_ID OF THE COPIED CHECKPOINT:
				copiedCheckpoint.CheckpointID = id * -1;
				copiedCheckpoint.Name = checkpoint.Name + ' (COPY)';
				copiedCheckpoint.isSelected = false;
				id = id + 1;
				copiedCheckpoints.push(copiedCheckpoint);
			}
		});

		setNewCheckpointCount(id); //UPDATING THE CREATED CHECKPOINT COUNT
		setCheckpoints([...checkpoints, ...copiedCheckpoints]);

		setSelectAll(false);
	};
	const onDeleteCheckpointClick = () => {
		const tempCheckpoints = checkpoints.filter(
			(checkpoint) =>
				!checkpoint.isSelected ||
				!checkpoint.hasOwnProperty('isSelected'),
		);
		setCheckpoints(tempCheckpoints);

		//REMOVING THE HIGHLIGHTED CHECKPOINT IF IT WAS DELETED:
		const isAnyCheckpointHighlighted = tempCheckpoints.some(
			(checkpoint) => checkpoint.CheckpointID === highlightedCheckpointID,
		);
		if (!isAnyCheckpointHighlighted) {
			setHighlightedCheckpointID(initCheckpoint.CheckpointID);
		}

		if (selectAll) {
			setSelectAll(false);
		}
	};
	const onSelectAllCheckpoints = () => {
		const tempCheckpoint = checkpoints.map((checkpoint) => ({
			...checkpoint,
			isSelected: !selectAll,
		}));
		setCheckpoints(tempCheckpoint);
		setSelectAll(!selectAll);
	};
	const onSelectCheckpointID = (checkpointID) => {
		const tempCheckpoints = checkpoints.map((checkpoint) => {
			if (checkpoint.CheckpointID === checkpointID) {
				return {
					...checkpoint,
					isSelected: !(checkpoint.isSelected || false),
				};
			}
			return checkpoint;
		});
		setCheckpoints(tempCheckpoints);
	};

	//This function is called when a checkpoint is clicked.
	//It checks the previous highlighted checkpoint name is valid or not.
	//If it is valid, it saves the checkpoint and changes the highlighted checkpoint to the clicked checkpoint.
	function onClickCheckpoint(clickedCheckpoint) {
		if (!inputValidationHelpers.checkCheckpointInputErrors()) {
			//INPUT VALIDATION IS COMPLETE, SAVING CHECKPOINT AND CHANGING THE HIGHLIGHTED CHECKPOINT.
			if (highlightedCheckpointID === clickedCheckpoint.CheckpointID) {
				setHighlightedCheckpointID(initCheckpoint.CheckpointID);
			} else {
				setHighlightedCheckpointID(clickedCheckpoint.CheckpointID);
			}
		} else {
			notifyWarning('Please fill the required fields.');
		}
	}

	return {
		selectAll,
		tableContainerRef,
		handleDragEnd,
		onNewCheckpointClick,
		onCopyCheckpointClick,
		onDeleteCheckpointClick,
		onSelectAllCheckpoints,
		onSelectCheckpointID,
		onClickCheckpoint,
	};
};

export default useCheckpointTable;
